import { Box, Button, HStack, Image, Input, Spinner, Text, Tooltip } from '@chakra-ui/react';
import { Icon } from '@blueprintjs/core';
import { useContext, useState } from 'react';
import MapContext from '../../context/MapContext';
import UIContext from '../../context/UIContext';
import { REPORT_CATEGORIES, reloadUserReportsSource } from '../../utils/userReports';

export default function ReportIssueDisplay() {
    const { reportIssueState, setReportIssueState, rulerControlRef, mapRef } = useContext(MapContext);
    const { createUserReport } = useContext(UIContext);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    if (!reportIssueState) {
        return null;
    }

    const closeTool = () => {
        setReportIssueState(null);
        setError(null);
    };

    const selectCategory = (categoryId) => {
        setError(null);
        setReportIssueState({
            ...reportIssueState,
            category: categoryId,
        });
    };

    const setDescription = (description) => {
        setReportIssueState({
            ...reportIssueState,
            description,
        });
    };

    const submitReport = async () => {
        if (!reportIssueState.category) {
            setError('Select a category');
            return;
        }
        if (reportIssueState.latitude == null || reportIssueState.longitude == null) {
            setError('Click the map to place a report');
            return;
        }

        setSubmitting(true);
        setError(null);
        try {
            if (rulerControlRef.current?.isMeasuring) {
                rulerControlRef.current.measuringOff();
            }
            const description = (reportIssueState.description || '').trim();
            await createUserReport({
                reportType: reportIssueState.category,
                position: {
                    latitude: reportIssueState.latitude,
                    longitude: reportIssueState.longitude,
                },
                properties: description ? { description } : undefined,
            });
            reloadUserReportsSource(mapRef.current);
            closeTool();
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || 'Failed to submit report');
        } finally {
            setSubmitting(false);
        }
    };

    const formatDms = (dd, longOrLat) => {
        const hemisphere = /^[WE]|(?:lon)/i.test(longOrLat)
            ? (dd < 0 ? 'W' : 'E')
            : (dd < 0 ? 'S' : 'N');
        const absDD = Math.abs(dd);
        const degrees = Math.floor(absDD);
        const minutes = Math.floor((absDD - degrees) * 60);
        const seconds = ((absDD - degrees - minutes / 60) * 3600).toFixed(2);
        return degrees + '°' + minutes + "'" + seconds + '" ' + hemisphere;
    };

    const positionText =
        reportIssueState.latitude != null && reportIssueState.longitude != null
            ? '(' + formatDms(reportIssueState.latitude, 'lat') + ', ' + formatDms(reportIssueState.longitude, 'lon') + ')'
            : 'Click map to place';

    return (
        <Box
            pointerEvents="auto"
            zIndex={20}
            display="flex"
            flexDir="column"
            justifyContent="stretch"
            alignItems="stretch"
            bg="rgba(255,255,255,0.9)"
            borderRadius="4px"
            border="1px solid rgba(0,0,0,0.1)"
            shadow="md"
            padding="0px"
            overflow="hidden"
            boxShadow={'0px 0px 0px 2px rgba(0,0,0,0.1)'}
            position="relative"
            minW="450px"
        >
            <HStack spacing={1} p="10px" pr="44px">
                <Text fontWeight="500">Report Issue</Text>
                <Text>-</Text>
                <Text fontFamily="monospace" fontWeight="400" fontSize="14px">
                    {positionText}
                </Text>
            </HStack>
            <HStack
                spacing={0}
                w="100%"
                borderTop="1px solid rgba(0,0,0,0.1)"
                m={0}
                p={0}
            >
                {REPORT_CATEGORIES.map((category) => {
                    const selected = reportIssueState.category === category.id;
                    return (
                        <Button
                            key={category.id}
                            flex="1"
                            size="sm"
                            h="36px"
                            m={0}
                            borderRadius={0}
                            variant="ghost"
                            bg="white"
                            color="black"
                            fontWeight={selected ? '500' : '400'}
                            borderRight="1px solid rgba(0,0,0,0.1)"
                            borderBottom={selected ? '3px solid #f59f00' : '3px solid transparent'}
                            _hover={{
                                bg: 'rgba(0,0,0,0.04)',
                            }}
                            _last={{
                                borderRight: 'none',
                            }}
                            onClick={() => selectCategory(category.id)}
                            isDisabled={submitting}
                            leftIcon={
                                <Image
                                    src={category.iconSrc}
                                    alt=""
                                    boxSize="16px"
                                />
                            }
                        >
                            {category.label}
                        </Button>
                    );
                })}
            </HStack>
            <Box
                bg="rgba(0,0,0,0.05)"
                w="100%"
                borderTop="1px solid rgba(0,0,0,0.1)"
                p="8px"
                display="flex"
                alignItems="center"
                gap="8px"
            >
                <Input
                    size="sm"
                    bg="white"
                    flex="1"
                    placeholder="Description (optional)"
                    value={reportIssueState.description || ''}
                    onChange={(e) => setDescription(e.target.value)}
                    isDisabled={submitting}
                    maxLength={500}
                />
                <Button
                    size="sm"
                    bg="#f59f00"
                    color="white"
                    flex="0 0 auto"
                    onClick={submitReport}
                    isDisabled={submitting}
                    _hover={{ bg: '#e67700' }}
                    _active={{ bg: '#e67700' }}
                    leftIcon={submitting ? <Spinner size="xs" /> : <Icon icon="send-message" size={14} />}
                >
                    Send
                </Button>
            </Box>
            {error && (
                <Text color="red.500" fontSize="13px" fontWeight="500" px="8px" pb="8px">
                    {error}
                </Text>
            )}
            <Tooltip placement="right" label={<Text>Close</Text>}>
                <Button
                    _hover={{
                        color: 'rgba(0,0,0,1.0)',
                    }}
                    bg={''}
                    w="40px"
                    h={'40px'}
                    position="absolute"
                    top="0px"
                    right="0px"
                    onClick={closeTool}
                    color={'rgba(0,0,0,0.8)'}
                    isDisabled={submitting}
                >
                    <Text mb="0px">
                        <Icon icon={'cross'} size="16" />
                    </Text>
                </Button>
            </Tooltip>
        </Box>
    );
}
